/**
 * AdminStockMovements
 *
 * Stok hareketlerinin (reserve/release/commit/inventur/wareneingang/
 * bruch/muster/eigenbedarf/conversion/adjustment/backorder) append-only
 * audit log'unu gösterir.
 *
 * Veri kaynağı: `stock_movements` tablosu — reserve_stock / release_stock
 * / commit_stock / adjust_stock / commit_inventur RPC'leri tarafından
 * yazılır. Doğrudan yazım yoktur; kayıtlar serileştirilmiş stok motoru
 * üzerinden gelir.
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ListOrdered, RefreshCw, Filter } from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const MOVEMENT_TYPES = [
  'reserve', 'release', 'commit',
  'inventur', 'wareneingang',
  'bruch', 'muster', 'eigenbedarf',
  'conversion', 'adjustment', 'backorder',
];

const TYPE_STYLES = {
  reserve:      { label: 'Reserve',      color: 'bg-blue-50 text-blue-700 border-blue-200' },
  release:      { label: 'Release',      color: 'bg-slate-100 text-slate-700 border-slate-200' },
  commit:       { label: 'Commit',       color: 'bg-red-50 text-red-700 border-red-200' },
  inventur:     { label: 'Inventur',     color: 'bg-purple-50 text-purple-700 border-purple-200' },
  wareneingang: { label: 'Eingang',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  bruch:        { label: 'Bruch',        color: 'bg-orange-50 text-orange-700 border-orange-200' },
  muster:       { label: 'Muster',       color: 'bg-amber-50 text-amber-700 border-amber-200' },
  eigenbedarf:  { label: 'Eigenbedarf',  color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
  conversion:   { label: 'Umwandlung',   color: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  adjustment:   { label: 'Adjustment',   color: 'bg-slate-100 text-slate-700 border-slate-200' },
  backorder:    { label: 'Backorder',    color: 'bg-red-100 text-red-800 border-red-300' },
};

export default function AdminStockMovements() {
  const [typeFilter, setTypeFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const { data: movements = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['stock-movements', typeFilter, productFilter, limit],
    queryFn: async () => {
      // SupabaseEntity.list sortField '-created_at' gibi bir alan kabul
      // ediyor. Biz son hareketler için created_at DESC istiyoruz ama
      // entity.list yalnızca basit select yapıyor — tüm kaydı çekip
      // client-side sıralıyoruz (tablo en fazla birkaç bin kayıt olur).
      const all = await base44.entities.StockMovement.list();
      return all
        .filter((m) => typeFilter === 'all' || m.type === typeFilter)
        .filter((m) => !productFilter || (m.product_id || '').includes(productFilter.trim()))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, limit);
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const productMap = useMemo(() => {
    const map = new Map();
    for (const p of products) map.set(p.id, p);
    return map;
  }, [products]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <ListOrdered className="w-5 h-5 text-slate-600" />
                  Bestandsbewegungen
                </h1>
                <p className="text-sm text-slate-500">
                  Audit-Log aller Stock-Engine Operationen
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
              Neu laden
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
        <Card className="mb-4">
          <CardContent className="p-4 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                <Filter className="w-3 h-3 inline mr-1" /> Typ
              </label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Alle Typen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Typen</SelectItem>
                  {MOVEMENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {TYPE_STYLES[t]?.label || t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                Produkt-ID (Teilstring)
              </label>
              <Input
                placeholder="UUID Anfang..."
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="h-9 font-mono text-xs"
              />
            </div>
            <div className="min-w-[120px]">
              <label className="text-xs font-semibold text-slate-500 uppercase block mb-1">
                Limit
              </label>
              <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                  <SelectItem value="500">500</SelectItem>
                  <SelectItem value="1000">1000</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {movements.length} Bewegung{movements.length === 1 ? '' : 'en'}
              {isLoading && ' · Lade...'}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr className="text-left">
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Zeit</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Typ</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Produkt</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Variante</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase text-right">Delta</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Quelle</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Benutzer</th>
                    <th className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase">Grund</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {movements.length === 0 && !isLoading && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                        Keine Bewegungen gefunden.
                      </td>
                    </tr>
                  )}
                  {movements.map((m) => {
                    const style = TYPE_STYLES[m.type] || TYPE_STYLES.adjustment;
                    const product = productMap.get(m.product_id);
                    const deltaPositive = (m.delta ?? 0) > 0;
                    return (
                      <tr key={m.id} className="hover:bg-slate-50">
                        <td className="px-4 py-2 text-xs text-slate-500 font-mono whitespace-nowrap">
                          {m.created_at
                            ? format(new Date(m.created_at), 'dd.MM.yy HH:mm:ss', { locale: de })
                            : '—'}
                        </td>
                        <td className="px-4 py-2">
                          <Badge variant="outline" className={`${style.color} font-semibold`}>
                            {style.label}
                          </Badge>
                        </td>
                        <td className="px-4 py-2">
                          <div className="text-sm font-medium text-slate-900">
                            {product?.name || (
                              <span className="font-mono text-xs text-slate-400">
                                {String(m.product_id).slice(0, 8)}…
                              </span>
                            )}
                          </div>
                          {product?.sku && (
                            <div className="text-xs text-slate-400 font-mono">{product.sku}</div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">
                          {m.variant_value || '—'}
                        </td>
                        <td className={`px-4 py-2 text-right font-mono font-bold ${deltaPositive ? 'text-emerald-700' : 'text-red-700'}`}>
                          {deltaPositive ? '+' : ''}{m.delta}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500">
                          {m.source_table || '—'}
                          {m.source_id && (
                            <span className="block font-mono text-[10px] text-slate-400">
                              {String(m.source_id).slice(0, 8)}…
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-600">
                          {m.actor_email || '—'}
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-500 max-w-[220px] truncate" title={m.reason || ''}>
                          {m.reason || '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
